'use client';

import Icon from '@/Icon';
import { LinkedBusinessRefresh } from '@/legacy/apis/repository/auth';
import { stepinAuthInstance } from '@/legacy/apis/types/stepinAuth';
import { deleteLayer } from '@/legacy/components/apps/ClientModalProvider';
import { clc } from '@/legacy/components/base/utils';
import ProfileImageV2 from '@/legacy/components/img/Profilev2';
import { KEBAB_KEY } from '@/legacy/components/modalContents/KebabModal';
import styles from '@/legacy/components/modalContents/profileModal.module.scss';
import { Cert } from '@/legacy/components/user/Cert';
import { BUSINESS_ACCOUNT_CREATE_PATHNAME, BUSINESS_ACCOUNT_MAIN_PATHNAME } from '@/legacy/constants/pathname';
import useLocaleLink from '@/legacy/hooks/useLocaleLink';
import { useTranslations } from 'next-intl';
import { ListSort, putOwnerId, useGetAllUsers, useGetCurrentOwner, type NewUserData } from '@/model/dexie/ownerDexie';
import { promiseNotNullCheck } from '@/utilities/stateTool';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import useSWR from 'swr';
import { useTransBusiness } from '@/@shared/hooks/useTranslations';

export type BusinessStatus = 'POSSIBLE' | 'IMPOSSIBLE' | 'WAITING';

// 커런트로 아이디 기반 호출
// 평탄화해서 저장하자
// 최대한 모든 데이터를 아이디 기반으로 전부 저장
// null 이여도 null 로저장
/** 데이터 계정 타입으로 authority 으로 구분시켜서 저장  */
const { GET } = stepinAuthInstance;
const getDone = async () => {
	// 스테이터스는 없는데... 비즈니스 헤더를 넣으면 조회 대상이 바뀜
	// 그래서 미들웨어에서 ignore 함
	const { data, error } = await GET('/user/business/status');
	if (data && data.data) {
		const status = data.data.status;
		return status as BusinessStatus;
	}
	if (error) throw error;

	return 'IMPOSSIBLE';
};

/** 뱃지 표시 교과서 */
const AccountCard = ({ id, profileImageUrl, authority, verifyStatus, nickname, stepinId }: NewUserData) => {
	const isCommon = authority === 'USER';

	return (
		<button
			className={styles.accountCard}
			onClick={async () => {
				// 모달 먼저 닫기
				deleteLayer(KEBAB_KEY);

				// 계정 전환 플래그 설정 (깜빡임 방지)
				localStorage.setItem('account-switching', Date.now().toString());

				// 계정 전환
				await putOwnerId(id);

				// 짧은 딜레이 후 새로고침 (Dexie 업데이트 완료 대기)
				setTimeout(() => {
					window.location.reload();
				}, 100);
			}}
		>
			<ProfileImageV2
				className={styles.accountImg}
				iconClass={styles.accountIcon}
				size={0}
				src={profileImageUrl}
				type={isCommon ? 'COMMON' : 'BUSINESS'}
			/>
			<div className={styles.accountInfo}>
				<div className={styles.accountTitle}>
					<span className={styles.nickname}>{nickname}</span>

					<Cert className={styles.accountIcon} isCommon={isCommon} verifyStatus={verifyStatus} />
				</div>
				<span className={styles.accountId}>@{stepinId}</span>
			</div>
		</button>
	);
	// console.log('account::', account)
};

const AccountList = ({ type, all }: { type: NewUserData['authority']; all: NewUserData[] }) => {
	const view = all.filter((item) => item.authority === type);
	return (
		<div className={styles.accounts} style={{ display: view.length === 0 ? 'none' : 'flex' }}>
			<span className={styles.accountsHeading}>{ListSort[type]}</span>
			{view.map((item) => (
				<AccountCard {...item} key={item.id} />
			))}
		</div>
	);
};

const ProfileModal = () => {
	const current = useGetCurrentOwner();
	const locale = useLocaleLink();

	const pathname = usePathname();
	const all = useGetAllUsers();
	const businessText = useTransBusiness('temp.inquire');

	const { data } = useSWR(['getBusinessStatusInfo'], () => getDone());

	// 현재 정보
	useEffect(() => {
		// 갱신 시키기
		LinkedBusinessRefresh();
	}, []);
	// getDoneTournament(status, page, limit)
	useEffect(() => {
		return () => {
			deleteLayer(KEBAB_KEY);
		};
	}, [pathname]);

	if (promiseNotNullCheck(all) && promiseNotNullCheck(current)) {
		const list = all.filter((item) => item.id !== current.id);
		const isOwner = current.authority === 'OWNER';
		const isUser = current.authority === 'USER';
		return (
			<div className={styles.space}>
				<div className={styles.currentProfile}>
					{isOwner ? (
						<Icon className={styles.crown} path="Sticker_Business_Crown_32" />
					) : (
						<div className={styles.crown} aria-hidden="true" />
					)}

					<span className={clc(styles.type, isOwner && styles.owner, !isUser && styles.cash)}>
						{ListSort[current.authority]}
					</span>
					<div className={styles.profile}>
						<ProfileImageV2
							className={styles.profileImg}
							iconClass={styles.profileIcon}
							size={0}
							src={current.profileImageUrl}
							type={current.authority === 'USER' ? 'COMMON' : 'BUSINESS'}
						/>

						<div className={styles.currentName}>
							<span className={styles.name}>{current.nickname}</span>
							<Cert className={styles.icon} isCommon={isUser} verifyStatus={current.verifyStatus} />
						</div>
						<span className={styles.stepinId}>{'@' + current.stepinId}</span>
					</div>
					<hr />
				</div>
				<AccountList all={list} type="USER" />
				<AccountList all={list} type="OWNER" />
				<AccountList all={list} type="ADMIN" />

				{data === 'POSSIBLE' && (
					<Link className={styles.button} href={BUSINESS_ACCOUNT_CREATE_PATHNAME(locale)}>
						<span className={styles.buttonText}>{businessText('applyForBusiness')}</span>
						<Icon className={styles.icon} color1={'#1A1A20'} path="Navi_Chevron_24_White_Right" />
					</Link>
				)}
				{data === 'WAITING' && (
					<Link className={clc(styles.button, styles.pending)} href={BUSINESS_ACCOUNT_MAIN_PATHNAME(locale)}>
						<span className={styles.buttonText}>{businessText('applyingForBusiness')}</span>
						<Icon className={styles.icon} color1={'#FDFDFE'} path="Navi_Chevron_24_White_Right" />
					</Link>
				)}
			</div>
		);
	}

	// 로딩 상태 표시
	return (
		<div className={styles.space} style={{ padding: '20px', textAlign: 'center' }}>
			<div style={{ marginBottom: '20px' }}>계정 정보를 불러오는 중...</div>
			<div style={{ fontSize: '12px', color: '#666' }}>비즈니스 계정이 있다면 잠시 후 계정 전환 옵션이 표시됩니다.</div>
		</div>
	);
};

export default ProfileModal;
